import { Component, Input, OnInit } from '@angular/core';
import { fuseAnimations } from '@fuse/animations';
import { MatDialog } from '@angular/material/dialog';
import { Subject, SubjectUserSecurity } from '../../../../shared/models/subject';
import { AuthService } from 'app/shared/services/auth.service';
import { enumReviewStatus } from 'app/shared/models/review';
import { AssessmentDialogComponent } from 'app/main/content/entities/assessment-dialog/assessment-dialog.component';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { EventService } from 'app/shared/services/event.service';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { User } from 'app/shared/models/user';
import { SubjectService } from 'app/shared/services/subject.service';
import { UserService } from 'app/shared/services/user.service';

@Component({
  selector: 'assessment-list-widget',
  templateUrl: './assessment-list-widget.component.html',
  styleUrls: ['./assessment-list-widget.component.scss'],
  animations: fuseAnimations,
})
export class AssessmentListWidgetComponent implements OnInit {
  _subject: Subject;
  title = '';
  data = [];
  selected: any;
  allowEdit = false;
  allowAdd = false;
  userReadOnly = false;
  hasSubjectUserEditPrivs = false;

  //added empty inputs to remove errors - YA
  @Input() id: Number;
  @Input() subjectId: Number;
  @Input() processTypeId: Number;
  @Input() settings: any;

  @Input() set subject(val: Subject) {
    if (val) {
      this.update(val);
    }
  }

  constructor(
    private authService: AuthService,
    private defaultDataService: DefaultDataService,
    private eventService: EventService,
    private subjectService: SubjectService,
    private userService: UserService,
    public dialog: MatDialog
  ) {
    this.userReadOnly = this.authService.isUserReadOnly();
    this.subjectService
      .getSubjectUserSecurity(this._subject, this.userService.getCurrentUserId())
      .subscribe((result: SubjectUserSecurity) => {
        if (!result || !result.restrictAccess || result.canEdit) {
          this.hasSubjectUserEditPrivs = true;
        }
      });
  }

  ngOnInit() {
    this.title = 'Assessments';
    this.allowEdit = this.getAllowEdit();
    this.allowAdd = this.getAllowAdd();
  }

  select(row) {
    this.selected = row;
  }

  add() {
    if (this.allowAdd) {
      this.openAssessment();
    }
  }

  edit(row) {
    if (
      this.allowEdit &&
      row &&
      row.subjectAssessment &&
      row.subjectAssessment.assessment
    ) {
      this.openAssessment(row.subjectAssessment.assessment);
    }
  }

  delete(row) {
    if (
      this.allowEdit &&
      row &&
      row.subjectAssessment &&
      row.subjectAssessment.assessment
    ) {
      const info = new MessageBoxParameters(
        'Are you sure you want to delete this assessment?',
        'Confirm',
        MessageBoxType.Confirm,
        true
      );
      this.dialog
        .open(MessageBoxComponent, {
          data: info,
          disableClose: false,
          autoFocus: false,
        })
        .afterClosed()
        .subscribe((result) => {
          if (result === 'confirm') {
            const assessmentId = row.subjectAssessment.assessmentId;
            this._subject.subjectAssessments =
              this._subject.subjectAssessments.filter(
                (a) => a.assessmentId !== assessmentId
              );
            this.subjectService.save(this._subject);
            this.update();
          }
        });
    }
  }

  openAssessment(assessment?) {
    this.dialog
      .open(AssessmentDialogComponent, {
        data: {
          assessment,
          subject: this._subject,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result && result.data && result.data.subject) {
          if (result.data.action === enumReviewStatus.Accepted) {
            this.eventService.acceptSubject(result.data.subject);
          }
          if (result.data.action === enumReviewStatus.Rejected) {
            this.eventService.rejectSubject(result.data.subject);
          }
          this.update(result.data.subject);
        }
      });
  }

  view(row) {
    // open read only assessment
    if (row && row.subjectAssessment && row.subjectAssessment.assessment) {
      this.dialog.open(AssessmentDialogComponent, {
        data: {
          assessment: row.subjectAssessment.assessment,
          subject: this._subject,
          readOnly: true,
        },
      });
    }
  }

  viewOrEdit(row) {
    if (this.allowEdit && !row.completed) {
      this.edit(row);
    } else {
      this.view(row);
    }
  }

  update(subject?: Subject) {
    subject = subject || this._subject;
    if (subject) {
      this._subject = subject;
    }
    this.allowAdd = this.getAllowAdd();
    if (this._subject) {
      this.allowEdit = this.getAllowEdit();
      const data = [];
      this._subject.subjectAssessments.map(
        (x) =>
          (x.assessment.lastModifiedDate =
            x.assessment.lastModifiedDate || x.assessment.completedDate)
      );
      this._subject.subjectAssessments.sort(
        (a, b) =>
          new Date(b.assessment.completedDate).getTime() -
            new Date(a.assessment.completedDate).getTime() ||
          new Date(b.assessment.lastModifiedDate).getTime() -
            new Date(a.assessment.lastModifiedDate).getTime()
      );
      this._subject.subjectAssessments.forEach((subjectAssessment) => {
        const a = subjectAssessment.assessment;
        if (a) {
          data.push({
            id: subjectAssessment.id,
            completed: a.completed,
            completedDate: a.completedDate,
            templateName: a.assessmentTemplate
              ? `${a.assessmentTemplate.name}`
              : '',
            secondary: a.completedBy
              ? 'Completed by: ' +
                (a.completedBy.fullName
                  ? a.completedBy.fullName
                  : new User(a.completedBy).fullName)
              : '',
            class: this.getClass(
              a.review && a.review.status
                ? a.review.status
                : a.assessmentTemplate && a.assessmentTemplate.autoReview
                ? enumReviewStatus.Pending
                : enumReviewStatus.None
            ),
            score: a.score.toString(),
            threshold: a.acceptanceThreshold.toString(),
            subjectAssessment,
          });
        }
      });
      this.data = data;
    }
  }

  getAllowAdd() {
    return (
      this.hasSubjectUserEditPrivs &&
      !this.userReadOnly &&
      (!this._subject || !this._subject.archived) &&
      this.defaultDataService.enableAssessments &&
      (!this._subject ||
        !(
          this._subject.isReferral &&
          this._subject.subjectReferrals &&
          this._subject.reviewStatus === enumReviewStatus.Accepted
        ))
    );
  }

  getAllowEdit() {
    return this.hasSubjectUserEditPrivs && !this.userReadOnly && (!this._subject || !this._subject.archived);
  }

  private getClass(status) {
    switch (status) {
      case enumReviewStatus.Accepted:
        return 'bg-green';
      case enumReviewStatus.Rejected:
        return 'bg-red';
      case enumReviewStatus.Pending:
        return 'bg-orange';
      default:
        return 'mat-accent-bg';
    }
  }

  trackById(index, item) {
    return item.id;
  }
}
